import { useEffect, useRef, useState } from 'react';
import { Button } from '../atoms';
import { ContractFilters, ContractListTable, initialContractFilters } from '../organism';
import { useNavigate } from 'react-router';
import { pathURL } from '@/components/constants/urls';
import { useUserLanguage, useListNavigationStateContext } from '@/customHooks';
import { ContractFilterInput, useGetContractsForListQuery } from '@/graphql/generated';
import { Pagination } from '../molecules';


const textsData = {
  en: {
    title: 'Tenant Contracts',
    buttonText: '+ Add Contract',
  },
  es: {
    title: 'Contratos de Inquilinos',
    buttonText: '+ Agregar Contrato',
  }
}

export const TenantContractsListPage = () => {
  const userLanguage = useUserLanguage();
  const texts = userLanguage === 'es' ? textsData.es : textsData.en;

  const navigate = useNavigate();
  const { getListState, setListState } = useListNavigationStateContext()
  const hasRestoredState = useRef(false)

  const [currentPage, setCurrentPage] = useState(() => {
    const savedState = getListState('contracts')
    return savedState?.currentPage ?? 1
  });
  const [filters, setFilters] = useState<ContractFilterInput>(() => {
    const savedState = getListState('contracts')
    return (savedState?.filters as ContractFilterInput) ?? initialContractFilters
  });

  useEffect(() => {
    if (!hasRestoredState.current) {
      const savedState = getListState('contracts')
      if (savedState) {
        setCurrentPage(savedState.currentPage)
        setFilters(savedState.filters as ContractFilterInput)
      }
      hasRestoredState.current = true
    }
  }, [getListState])

  useEffect(() => {
    if (hasRestoredState.current) {
      setListState('contracts', {
        currentPage,
        filters,
      })
    }
  }, [currentPage, filters, setListState])

  const handleFilterChange = (newFilters: ContractFilterInput) => {
    setFilters(newFilters);
    setCurrentPage(1);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    setFilters({ ...filters, page: page.toString() });
  };

  const normalizedFilters: ContractFilterInput = {
    ...filters,
    page: currentPage.toString(),
    startMonth: filters.startMonth != null ? String(filters.startMonth) : null,
    endMonth: filters.endMonth != null ? String(filters.endMonth) : null,
  };

  const { data } = useGetContractsForListQuery({
    variables: {
      input: normalizedFilters,
    },
    fetchPolicy: 'network-only',
  });

  const currentDataCount = data?.contracts?.items?.length || 0;
  const totalPages = data?.contracts?.pagination?.totalPages || 1;
  const showPagination = currentDataCount > 0;

  return (
    <div className="w-full flex flex-col gap-6 p-4">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold bg-linear-to-r from-primary1 to-primary2 bg-clip-text text-transparent">
          {texts.title}
        </h2>
        <div>
          <Button
            onClick={() => navigate(`/${pathURL.USER}/${pathURL.CONTRACT}/${pathURL.TENANT}/${pathURL.CREATE}`)}
          >
            {texts.buttonText}
          </Button>
        </div>
      </div>
      <div>
        <ContractFilters filters={filters} onFilterChange={handleFilterChange} />
      </div>
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        <ContractListTable filters={{ ...normalizedFilters }} />
      </div>
      {showPagination && (
        <div className="mt-4">
          <Pagination
            currentPage={currentPage}
            onPageChange={handlePageChange}
            currentDataCount={currentDataCount}
            pageSize={filters.pageSize || null}
            totalPages={totalPages}
          />
        </div>
      )}
    </div>
  );
}
